import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import api from "@/lib/api";
import { toast } from "sonner";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Loader2, AlertTriangle, ExternalLink, FileText } from "lucide-react";

const KYB_STATUSES = ["draft", "submitted", "activated", "rejected", "additional_info_requested"];
const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export default function AdminUserDetail() {
  const { id } = useParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/admin/users/${id}`);
      setData(data);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not load user.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  if (loading || !data) return <p className="text-sm text-slate-500">Loading user…</p>;

  return (
    <div className="space-y-6 max-w-5xl" data-testid="admin-user-detail">
      <Link to="/admin/users" className="text-sm text-slate-500 hover:text-slate-800">
        ← Back to users
      </Link>

      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold text-slate-900" data-testid="admin-user-name">
          {data.profile?.legal_name || <span className="italic">— unnamed —</span>}
        </h1>
        <p className="text-sm text-slate-500 mt-1">
          {data.email} · Onboarded {data.onboarded_at ? new Date(data.onboarded_at).toLocaleDateString("en-IN") : "—"}
        </p>
      </div>

      {/* Status correction */}
      <KybStatusCorrection user={data} onDone={load} />

      {/* Profile + business details */}
      <Card>
        <CardContent className="p-6 space-y-1">
          <p className="text-xs uppercase tracking-wider text-slate-500 font-semibold mb-3">Business profile</p>
          <DetailRow label="Business type" value={data.profile?.business_type} />
          <DetailRow label="Owner name" value={data.profile?.owner_name} />
          <DetailRow label="Phone" value={data.profile?.phone} />
          <DetailRow label="Primary product" value={data.profile?.primary_product} />
          <DetailRow label="GSTIN" value={data.profile?.tax_gst} mono />
          <DetailRow label="Business ID (CIN / LLP ID)" value={data.profile?.business_id} mono />
          <DetailRow label="Business PAN" value={data.profile?.tax_id} mono />
          <DetailRow label="Xflow account ID" value={data.xflow_account_id} mono />
        </CardContent>
      </Card>

      {/* Payout */}
      {data.payout && (
        <Card>
          <CardContent className="p-6 space-y-1">
            <p className="text-xs uppercase tracking-wider text-slate-500 font-semibold mb-3">Payout bank</p>
            <DetailRow label="Holder" value={data.payout.holder_name} />
            <DetailRow label="Bank" value={data.payout.bank_name} />
            <DetailRow label="IFSC" value={data.payout.ifsc} mono />
            <DetailRow label="Account (masked)" value={data.payout.number_masked} mono />
          </CardContent>
        </Card>
      )}

      {/* KYB payload */}
      {data.kyb && Object.keys(data.kyb).length > 0 && (
        <Card>
          <CardContent className="p-6">
            <p className="text-xs uppercase tracking-wider text-slate-500 font-semibold mb-3">
              KYB submission ({data.kyb.entity_type || "—"})
            </p>
            <pre
              className="text-xs bg-slate-50 border rounded-md p-3 overflow-auto max-h-96"
              data-testid="admin-kyb-json"
            >
              {JSON.stringify(data.kyb, null, 2)}
            </pre>
          </CardContent>
        </Card>
      )}

      {/* KYB documents */}
      <Card>
        <CardContent className="p-6">
          <p className="text-xs uppercase tracking-wider text-slate-500 font-semibold mb-3">
            KYB documents ({data.kyb_documents.length})
          </p>
          {data.kyb_documents.length === 0 ? (
            <p className="text-sm text-slate-400 italic">No documents uploaded.</p>
          ) : (
            <div className="space-y-2">
              {data.kyb_documents.map((d) => (
                <DocumentRow key={d.file_id} doc={d} />
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Transactions */}
      <Card>
        <CardContent className="p-6">
          <p className="text-xs uppercase tracking-wider text-slate-500 font-semibold mb-3">
            Transactions ({data.transactions.length})
          </p>
          {data.transactions.length === 0 ? (
            <p className="text-sm text-slate-400 italic">No transactions yet.</p>
          ) : (
            <table className="w-full text-sm">
              <thead className="text-xs uppercase text-slate-500">
                <tr>
                  <th className="text-left py-2">Date</th>
                  <th className="text-left py-2">Sender</th>
                  <th className="text-right py-2">USD</th>
                  <th className="text-right py-2">INR</th>
                  <th className="text-left py-2 pl-3">Withdrawal</th>
                </tr>
              </thead>
              <tbody>
                {data.transactions.map((t) => (
                  <tr key={t.id} className="border-b last:border-0" data-testid={`admin-txn-${t.id}`}>
                    <td className="py-2 text-slate-500 text-xs">
                      {t.settlement_date ? String(t.settlement_date).slice(0, 10) : "—"}
                    </td>
                    <td className="py-2">{t.sender_name}</td>
                    <td className="py-2 text-right sf-mono">${Number(t.amount_usd).toLocaleString("en-IN")}</td>
                    <td className="py-2 text-right sf-mono">
                      ₹{Number(t.amount_inr).toLocaleString("en-IN")}
                    </td>
                    <td className="py-2 pl-3 text-xs text-slate-500">
                      {(t.withdrawal?.status || t.withdrawal_status || "not_started").replaceAll("_", " ")}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </CardContent>
      </Card>

      {/* Audit log */}
      <Card>
        <CardContent className="p-6">
          <p className="text-xs uppercase tracking-wider text-slate-500 font-semibold mb-3">
            Admin audit log ({data.audit_log.length})
          </p>
          {data.audit_log.length === 0 ? (
            <p className="text-sm text-slate-400 italic">No admin actions on this user yet.</p>
          ) : (
            <div className="space-y-3">
              {data.audit_log.map((a) => (
                <div key={a.id} className="text-sm border-l-2 border-slate-200 pl-3" data-testid={`admin-audit-${a.id}`}>
                  <div className="flex justify-between text-xs text-slate-500">
                    <span>{new Date(a.timestamp).toLocaleString("en-IN")}</span>
                    <span className="font-medium">{a.admin_email}</span>
                  </div>
                  <p className="mt-0.5 text-slate-800">
                    {a.action === "correct_kyb_status" ? (
                      <>
                        Corrected KYB status:{" "}
                        <code className="text-xs bg-slate-100 px-1 rounded">{String(a.old_value)}</code>{" "}
                        →{" "}
                        <code className="text-xs bg-slate-100 px-1 rounded">{String(a.new_value)}</code>
                      </>
                    ) : (
                      <>{a.action}</>
                    )}
                  </p>
                  {a.reason && <p className="mt-1 text-xs text-slate-600 italic">{`"${a.reason}"`}</p>}
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

// ---------------------------------------------------------------------------
// KYB status correction card
// ---------------------------------------------------------------------------
function KybStatusCorrection({ user, onDone }) {
  const [newStatus, setNewStatus] = useState(user.kyb_status || "draft");
  const [reason, setReason] = useState("");
  const [saving, setSaving] = useState(false);

  const submit = async () => {
    if (reason.trim().length < 10) {
      toast.error("Please write at least 10 characters justifying this correction.");
      return;
    }
    setSaving(true);
    try {
      await api.post(`/admin/users/${user.id}/kyb-status`, {
        new_status: newStatus,
        reason: reason.trim(),
      });
      toast.success(`KYB status corrected to '${newStatus}'.`);
      setReason("");
      onDone();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not save correction.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Card className="border-amber-200 bg-amber-50/40">
      <CardContent className="p-6">
        <div className="flex items-start gap-3 mb-4">
          <AlertTriangle className="h-5 w-5 text-amber-600 shrink-0 mt-0.5" />
          <div>
            <p className="text-sm font-semibold text-slate-900">KYB status correction (not activation)</p>
            <p className="text-xs text-slate-600 mt-1 max-w-2xl leading-relaxed">
              Use this only to fix a sync mismatch when you&apos;ve independently confirmed the actual status with Xflow support
              (e.g. our webhook missed an update). This writes to our local DB only — <strong>it does not call Xflow.</strong>
            </p>
          </div>
        </div>

        <div className="grid md:grid-cols-2 gap-4">
          <div className="space-y-1">
            <Label className="text-xs uppercase tracking-wider text-slate-600 font-semibold">
              Current status
            </Label>
            <p className="h-10 flex items-center px-3 rounded-md border border-slate-200 bg-white text-sm text-slate-600" data-testid="admin-kyb-current">
              {user.kyb_status || "—"}
            </p>
          </div>
          <div className="space-y-1">
            <Label className="text-xs uppercase tracking-wider text-slate-600 font-semibold">Correct to</Label>
            <select
              value={newStatus}
              onChange={(e) => setNewStatus(e.target.value)}
              data-testid="admin-kyb-new-status"
              className="w-full h-10 px-3 rounded-md border border-slate-300 text-sm bg-white"
            >
              {KYB_STATUSES.map((s) => (
                <option key={s} value={s}>
                  {s.replaceAll("_", " ")}
                </option>
              ))}
            </select>
          </div>
        </div>

        <div className="mt-4">
          <Label className="text-xs uppercase tracking-wider text-slate-600 font-semibold">
            Reason (required, min 10 chars)
          </Label>
          <textarea
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            placeholder="Confirmed with Xflow support ticket #12345 on 2026-02-16 — account is genuinely activated, our webhook missed it."
            className="w-full min-h-24 mt-1 px-3 py-2 rounded-md border border-slate-300 text-sm"
            data-testid="admin-kyb-reason"
          />
          <p className="text-xs text-slate-500 mt-1">
            This reason is stored permanently in the audit log with your email + timestamp.
          </p>
        </div>

        <div className="mt-4 flex justify-end">
          <Button
            onClick={submit}
            disabled={saving || newStatus === user.kyb_status || reason.trim().length < 10}
            data-testid="admin-kyb-submit"
          >
            {saving ? (
              <>
                <Loader2 className="h-4 w-4 mr-2 animate-spin" /> Saving…
              </>
            ) : (
              <>Record correction</>
            )}
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}

function DetailRow({ label, value, mono }) {
  return (
    <div className="flex justify-between py-2 border-b last:border-0 text-sm">
      <span className="text-slate-600">{label}</span>
      <span className={`text-slate-900 font-medium ${mono ? "sf-mono" : ""}`}>{value || <span className="text-slate-300">—</span>}</span>
    </div>
  );
}

function DocumentRow({ doc }) {
  const token = localStorage.getItem("sf_token");
  const url = `${BACKEND_URL}/api/admin/documents/${doc.file_id}`;

  const download = async () => {
    try {
      const res = await fetch(url, { headers: token ? { Authorization: `Bearer ${token}` } : {} });
      if (!res.ok) throw new Error("failed");
      const blob = await res.blob();
      const u = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = u;
      a.download = doc.filename || `${doc.file_id}.bin`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(u);
    } catch {
      toast.error("Download failed.");
    }
  };

  const view = async () => {
    try {
      const res = await fetch(url, { headers: token ? { Authorization: `Bearer ${token}` } : {} });
      if (!res.ok) throw new Error("failed");
      const blob = await res.blob();
      const u = URL.createObjectURL(blob);
      window.open(u, "_blank");
    } catch {
      toast.error("Preview failed.");
    }
  };

  return (
    <div className="flex items-center justify-between bg-slate-50 border rounded-md px-3 py-2" data-testid={`admin-doc-${doc.file_id}`}>
      <div className="flex items-center gap-2 min-w-0">
        <FileText className="h-4 w-4 text-slate-500 shrink-0" />
        <div className="min-w-0">
          <p className="text-sm font-medium truncate">{doc.filename || doc.file_id}</p>
          <p className="text-xs text-slate-500">
            {doc.mime_type} · {(doc.size_bytes / 1024).toFixed(1)} KB · uploaded {new Date(doc.uploaded_at).toLocaleDateString("en-IN")}
          </p>
        </div>
      </div>
      <div className="flex items-center gap-2 shrink-0">
        <button onClick={view} className="text-xs text-blue-600 hover:underline flex items-center gap-1" data-testid={`admin-doc-view-${doc.file_id}`}>
          View <ExternalLink className="h-3 w-3" />
        </button>
        <button onClick={download} className="text-xs text-slate-500 hover:text-slate-800" data-testid={`admin-doc-download-${doc.file_id}`}>
          Download
        </button>
      </div>
    </div>
  );
}
